/**
 * Types + helpers for the DIFOT report.
 *
 * Data flows: React → Worker (/api/reports/difot*) → Supabase. All aggregation
 * happens in Postgres (difot_report() / v_difot_stops); the browser only shapes
 * and renders.
 */
import { apiFetch } from './api';
import { businessTodayISO, businessYesterdayISO } from './dates';

export interface DifotTotals {
  total: number;
  completed: number;
  failed: number;
  pending: number;
  cancelled: number;
  /** Anything still moving: Arrived, Enroute, On Hold, Delayed. */
  in_progress: number;
  /** Attempted — every stop that wasn't cancelled. The DIFOT denominator. */
  denom: number;
  /** Strict measure: Complete AND in full AND completed on the planned day. Not what the KPI card shows. */
  difot: number;
  /** Completions that had an SMS delivery window on file — the On Time denominator. */
  completed_with_window: number;
  /** Completions that landed inside that window — the On Time numerator. */
  in_window_n: number;
  /** Completions that finished on their planned trip_date (the DIFOT "on time" leg). */
  on_time_n: number;
  drivers: number;
  avg_service: number | null;
}

export interface DifotDaily {
  d: string; // YYYY-MM-DD
  total: number;
  completed: number;
  failed: number;
  pending: number;
  denom: number;
  difot: number;
  with_window: number;
  in_window_n: number;
}

export interface DifotStateRow {
  state: string;
  total: number;
  complete: number;
  failed: number;
  pending: number;
  denom: number;
  difot: number;
  with_window: number;
  in_window_n: number;
}

export interface DifotDriverRow {
  driver: string;
  team: string | null;
  total: number;
  completed: number;
  failed: number;
  denom: number;
  with_window: number;
  in_window_n: number;
  on_time_n: number;
  difot: number;
  avg_service: number | null;
}

export interface DifotReport {
  totals: DifotTotals;
  daily: DifotDaily[];
  by_state: DifotStateRow[];
  drivers: DifotDriverRow[];
  options: { states: string[]; drivers: string[] };
  /** Most recent trip_date with attempted deliveries — used to anchor relative ranges. */
  latest: string | null;
}

export interface DifotStop {
  stop_id: number;
  so: string | null;
  ref: string | null;
  status: string;
  trip_date: string;
  driver: string | null;
  team: string | null;
  state: string | null;
  region: string | null;
  item_quantity: number | null;
  enroute_time: string | null;
  arrived_time: string | null;
  completed_time: string | null;
  failed_time: string | null;
  distance_km: number | null;
  service_min: number | null;
  travel_min: number | null;
  w_start: string | null;
  w_end: string | null;
  /** Completed on the planned trip_date (the DIFOT "on time" leg). */
  on_time: boolean | null;
  /** Completed inside the SMS delivery window; null when the stop had no window. */
  in_window: boolean | null;
  in_full: boolean | null;
  fail_note: string | null;
  address: string | null;
}

export type DateRange = 'all' | 'today' | 'yesterday' | 'last7' | 'last30' | 'custom';
export type Period = 'daily' | 'weekly' | 'monthly';

/** Inclusive custom bounds (YYYY-MM-DD), only meaningful when range === 'custom'. */
export interface CustomRange {
  from?: string;
  to?: string;
}

export interface Filters extends CustomRange {
  range: DateRange;
  state: string;
  /** Selected drivers / runs. Empty means every driver. */
  drivers: string[];
  status: string;
}

/**
 * Convert a UI date range into from/to bounds anchored to the live business day
 * (Australia/Sydney). "Today" means the real current calendar day — matched on
 * trip_date — not the most recent day that happens to have data.
 *
 * For `custom`, the caller's own bounds are used verbatim (swapped if entered
 * back-to-front); a missing bound stays null so the window is open-ended on
 * that side.
 */
export function rangeToDates(range: DateRange, custom?: CustomRange): { from: string | null; to: string | null } {
  if (range === 'custom') {
    const from = custom?.from?.trim() || null;
    const to = custom?.to?.trim() || null;
    return from && to && from > to ? { from: to, to: from } : { from, to };
  }
  if (range === 'all') return { from: null, to: null };
  if (range === 'yesterday') {
    const y = businessYesterdayISO();
    return { from: y, to: y };
  }
  const today = businessTodayISO();
  const t = new Date(`${today}T00:00:00Z`);
  const iso = (d: Date) => d.toISOString().slice(0, 10);
  const minus = (d: Date, days: number) => {
    const x = new Date(d);
    x.setUTCDate(x.getUTCDate() - days);
    return x;
  };
  switch (range) {
    case 'today':
      return { from: today, to: today };
    case 'last7':
      return { from: iso(minus(t, 6)), to: today };
    case 'last30':
      return { from: iso(minus(t, 29)), to: today };
    default:
      return { from: null, to: null };
  }
}

/**
 * Starting bounds when the user switches the picker to "Custom" — seed it with
 * the window they were already looking at so the report doesn't jump, falling
 * back to the last 30 days when the previous window was open-ended ("all").
 */
export function defaultCustomRange(previous: DateRange, current: CustomRange): Required<CustomRange> {
  if (current.from && current.to) return { from: current.from, to: current.to };
  const seeded = rangeToDates(previous, current);
  const fallback = rangeToDates('last30');
  return {
    from: seeded.from ?? current.from ?? fallback.from ?? businessTodayISO(),
    to: seeded.to ?? current.to ?? businessTodayISO(),
  };
}

/**
 * Build a query string, dropping empty values. Array values are repeated
 * (`?driver=A&driver=B`) rather than joined, since driver names can themselves
 * contain a comma.
 */
function queryString(params: Record<string, string | string[] | null>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v == null) continue;
    if (Array.isArray(v)) {
      for (const item of v) if (item !== '') sp.append(k, item);
    } else if (v !== '') {
      sp.set(k, v);
    }
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}

export function fetchReport(filters: Filters): Promise<DifotReport> {
  const { from, to } = rangeToDates(filters.range, filters);
  return apiFetch<DifotReport>(
    `/api/reports/difot${queryString({
      from,
      to,
      state: filters.state,
      driver: filters.drivers,
      status: filters.status,
    })}`,
  );
}

export function fetchStops(
  params: {
    from?: string | null;
    to?: string | null;
    state?: string;
    drivers?: string[];
    status?: string;
    q?: string;
    limit?: number;
  },
): Promise<DifotStop[]> {
  return apiFetch<DifotStop[]>(
    `/api/reports/difot/stops${queryString({
      from: params.from ?? null,
      to: params.to ?? null,
      state: params.state ?? null,
      driver: params.drivers ?? null,
      status: params.status ?? null,
      q: params.q ?? null,
      limit: params.limit != null ? String(params.limit) : null,
    })}`,
  );
}

// ─── Failure categorisation (ported from the dashboard mockup) ──────────────

export interface FailCategory {
  key: string;
  label: string;
  color: string;
  patterns: RegExp[];
}

export const FAIL_CATEGORIES: FailCategory[] = [
  {
    key: 'customer_unavailable',
    label: 'Customer unavailable',
    color: '#B37D6C',
    patterns: [/not home/i, /no answer/i, /no one home/i, /switched off/i, /didn.?t answer/i, /not avail/i, /couldn.?t get a hold/i, /no.?one was home/i, /not there/i, /wasn.?t at home/i, /no information provided/i],
  },
  {
    key: 'reschedule',
    label: 'Rescheduled / cancelled',
    color: '#FF9500',
    patterns: [/reschedule/i, /re ?schedule/i, /cancel/i, /didn.?t book/i, /not ready/i, /later date/i, /lunch time/i, /will be delivered at a later/i, /refer to store/i, /store called/i],
  },
  {
    key: 'damage',
    label: 'Damage / refused',
    color: '#FF5245',
    patterns: [/damage/i, /refused/i, /dust inside/i, /swap.?over/i, /not happy/i, /return/i, /broken/i],
  },
  {
    key: 'access',
    label: 'Access / loading',
    color: '#0061A2',
    patterns: [/loading/i, /dock/i, /access/i, /can.?t get in/i, /no parking/i, /gate/i, /locked/i],
  },
  {
    key: 'address',
    label: 'Address / location',
    color: '#1A85C9',
    patterns: [/wrong address/i, /can.?t find/i, /address.*not/i, /no such/i, /incorrect address/i],
  },
  { key: 'other', label: 'Other / uncategorised', color: '#737474', patterns: [] },
];

export function categoriseFailure(note: string | null): string {
  if (!note || !note.trim()) return 'other';
  for (const c of FAIL_CATEGORIES) {
    if (c.key === 'other') continue;
    if (c.patterns.some((p) => p.test(note))) return c.key;
  }
  return 'other';
}

// ─── Period bucketing for the trend chart ──────────────────────────────────

export function periodKey(dateStr: string, period: Period): string {
  if (period === 'daily') return dateStr;
  const d = new Date(`${dateStr}T00:00:00Z`);
  if (period === 'weekly') {
    const day = d.getUTCDay() || 7; // Mon=1..Sun=7
    d.setUTCDate(d.getUTCDate() - day + 1);
    return d.toISOString().slice(0, 10);
  }
  return `${dateStr.slice(0, 7)}-01`; // monthly
}

export function formatPeriodLabel(key: string, period: Period): string {
  const d = new Date(`${key}T00:00:00Z`);
  if (period === 'monthly') {
    return d.toLocaleDateString('en-AU', { month: 'short', year: '2-digit', timeZone: 'UTC' });
  }
  if (period === 'weekly') {
    return `Wk ${d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', timeZone: 'UTC' })}`;
  }
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', timeZone: 'UTC' });
}

export function formatDateAU(s: string | null): string {
  if (!s) return '—';
  return new Date(s.length <= 10 ? `${s}T00:00:00Z` : s).toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'Australia/Sydney',
  });
}

export const STATE_COLOURS: Record<string, string> = {
  NSW: '#0061A2',
  VIC: '#1A85C9',
  QLD: '#FF9500',
  ACT: '#0BDA8E',
  WA: '#B37D6C',
  SA: '#003D6C',
  TAS: '#384553',
  NT: '#FF5245',
  Unknown: '#C5CFDD',
};

export function pct(n: number, d: number): number {
  return d ? (n / d) * 100 : 0;
}
